import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { MailIcon, LockIcon, CheckIcon } from '../components/Icons';
import { API_ENDPOINTS } from '../config/api';

export const PasswordResetPage = () => {
  // Determine view based on URL token
  const [view, setView] = useState('request'); // 'request', 'sent', 'reset', 'success'
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    // Check if there's a token in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    if (resetToken) {
      setToken(resetToken);
      setView('reset');
    }
  }, []);

  const handleRequestReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailError('');

    if (!email) {
      setEmailError('Email is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Please enter a valid email');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.auth.forgotPassword, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setEmailError(data.error || 'Failed to send reset email');
        setLoading(false);
        return;
      }

      console.log('Password reset email sent');
      setView('sent');
      setLoading(false);
    } catch (error) {
      console.error('Password reset request error:', error);
      setEmailError('Network error. Please try again.');
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPasswordError('');
    setConfirmError('');

    let hasError = false;

    if (!newPassword) {
      setPasswordError('Password is required');
      hasError = true;
    } else if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      hasError = true;
    } else if (!/[A-Z]/.test(newPassword)) {
      setPasswordError('Password must contain at least one uppercase letter');
      hasError = true;
    } else if (!/[a-z]/.test(newPassword)) {
      setPasswordError('Password must contain at least one lowercase letter');
      hasError = true;
    } else if (!/[0-9]/.test(newPassword)) {
      setPasswordError('Password must contain at least one number');
      hasError = true;
    }

    if (newPassword !== confirmPassword) {
      setConfirmError('Passwords do not match');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.auth.resetPassword, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setPasswordError(data.error || 'Failed to reset password');
        setLoading(false);
        return;
      }

      console.log('Password reset successfully');
      setView('success');
      setLoading(false);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      setPasswordError('Network error. Please try again.');
      setLoading(false);
    }
  };

  // Request Reset View
  if (view === 'request') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Header showAuth={false} />

        <main style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
        }}>
          <div style={{
            maxWidth: '480px',
            width: '100%',
          }}>
            {/* Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: '40px',
            }}>
              <h1 style={{
                fontSize: '40px',
                fontWeight: '400',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Reset Your Password
              </h1>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Enter your email and we'll send you a reset link
              </p>
            </div>

            {/* Form Card */}
            <div style={{
              backgroundColor: theme.colors.white,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.xlarge,
              padding: '40px',
            }}>
              <form onSubmit={handleRequestReset}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                }}>
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={emailError}
                    icon={<MailIcon />}
                    required
                  />

                  <Button type="submit" fullWidth size="large">
                    Send Reset Link
                  </Button>
                </div>
              </form>
            </div>

            {/* Back to Login */}
            <div style={{
              textAlign: 'center',
              marginTop: '24px',
              fontSize: '15px',
              color: theme.colors.textSecondary,
            }}>
              Remember your password?{' '}
              <a href="/login" style={{
                color: theme.colors.primary,
                textDecoration: 'none',
                fontWeight: '500',
              }}>
                Sign in
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Email Sent View
  if (view === 'sent') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Header showAuth={false} />

        <main style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
          }}>
            {/* Icon */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              backgroundColor: theme.colors.primaryLight,
              borderRadius: '50%',
              color: theme.colors.primary,
              marginBottom: '32px',
            }}>
              <MailIcon width={40} height={40} />
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '16px',
              color: theme.colors.textPrimary,
            }}>
              Check Your Email
            </h1>

            {/* Description */}
            <p style={{
              fontSize: '18px',
              lineHeight: '1.6',
              color: theme.colors.textSecondary,
              marginBottom: '32px',
            }}>
              We've sent a password reset link to
            </p>

            {/* Email Display */}
            <div style={{
              backgroundColor: theme.colors.white,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.large,
              padding: '16px 24px',
              marginBottom: '32px',
              display: 'inline-block',
            }}>
              <span style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
                fontFamily: theme.fonts.monospace,
              }}>
                {email}
              </span>
            </div>

            {/* Instructions */}
            <div style={{
              backgroundColor: theme.colors.white,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.xlarge,
              padding: '32px',
              marginBottom: '32px',
              textAlign: 'left',
            }}>
              <p style={{
                fontSize: '16px',
                lineHeight: '1.6',
                color: theme.colors.textSecondary,
                margin: 0,
              }}>
                Click the link in the email to reset your password. The link will expire in 24 hours.
                If you don't see the email, check your spam folder.
              </p>
            </div>

            {/* Actions */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'center',
            }}>
              <Button onClick={() => setView('request')}>
                Try Another Email
              </Button>
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>
                Back to Login
              </Button>
            </div>

            {/* Demo button to simulate clicking reset link */}
            <div style={{ marginTop: '32px', padding: '16px', backgroundColor: theme.colors.backgroundAlt, borderRadius: theme.borderRadius.large }}>
              <small style={{ color: theme.colors.textTertiary }}>Demo: Simulate clicking email link</small>
              <br />
              <Button variant="outline" size="small" onClick={() => setView('reset')} style={{ marginTop: '8px' }}>
                Go to Reset Password Form
              </Button>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Reset Password Form View
  if (view === 'reset') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Header showAuth={false} />

        <main style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
        }}>
          <div style={{
            maxWidth: '480px',
            width: '100%',
          }}>
            {/* Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: '40px',
            }}>
              <h1 style={{
                fontSize: '40px',
                fontWeight: '400',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Set New Password
              </h1>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Enter a new password for your account
              </p>
            </div>

            {/* Form Card */}
            <div style={{
              backgroundColor: theme.colors.white,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.xlarge,
              padding: '40px',
            }}>
              <form onSubmit={handleResetPassword}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}>
                  <Input
                    label="New Password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    error={passwordError}
                    icon={<LockIcon />}
                    helperText="Choose a strong password with letters, numbers, and symbols"
                    required
                  />

                  <Input
                    label="Confirm New Password"
                    type="password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={confirmError}
                    icon={<LockIcon />}
                    required
                  />

                  <Button type="submit" fullWidth size="large">
                    Reset Password
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Success View
  if (view === 'success') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Header showAuth={false} />

        <main style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
          }}>
            {/* Success Icon */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              backgroundColor: theme.colors.primaryLight,
              borderRadius: '50%',
              color: theme.colors.primary,
              marginBottom: '32px',
            }}>
              <CheckIcon width={40} height={40} />
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '16px',
              color: theme.colors.textPrimary,
            }}>
              Password Reset Successfully
            </h1>

            {/* Description */}
            <p style={{
              fontSize: '18px',
              lineHeight: '1.6',
              color: theme.colors.textSecondary,
              marginBottom: '32px',
            }}>
              Your password has been updated. You can now sign in with your new password.
            </p>

            {/* Redirect Message */}
            <div style={{
              backgroundColor: theme.colors.primaryLight,
              borderRadius: theme.borderRadius.large,
              padding: '16px',
              marginBottom: '24px',
              fontSize: '15px',
              color: theme.colors.primaryDark,
            }}>
              Redirecting to login page in 3 seconds...
            </div>

            {/* Action */}
            <Button size="large" onClick={() => window.location.href = '/login'}>
              Go to Login
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return null;
};
